import React from 'react';
import { Image, Linking, Platform, StyleSheet, TouchableOpacity } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import type { AvailableIconName } from '@/assets/icons';
import { imgNotiBell } from '@/assets/images';
import { Scroll, Stack } from '@/components/layout';
import { showAlert, SvgIcon, Switch, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useBiometric } from '@/features/auth/hooks/use-biometric';
import type { auth } from '@/features/auth/types';
import { createStyles, useNotificationPermission, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.onboarding.step4;

/** Icon-circle background + warning callout colours that aren't in the shared palette yet. */
const THUMB_BG = '#E8F1FF';
const WARNING_BORDER = '#FFAA00';
const WARNING_GRADIENT = ['#FFF2D8', '#FFF8E9'];

/** The five notification opt-ins, in Figma order. `key` maps to the API payload field. */
const NOTIFICATION_ROWS: {
  key: keyof auth.NotificationPreferences;
  icon: AvailableIconName;
  title: string;
  subtitle: string;
}[] = [
  {
    key: 'classReminders',
    icon: 'IcNotification',
    title: strings.classRemindersTitle,
    subtitle: strings.classRemindersSubtitle,
  },
  {
    key: 'coachNotes',
    icon: 'IcNote',
    title: strings.coachNotesTitle,
    subtitle: strings.coachNotesSubtitle,
  },
  {
    key: 'tournamentUpdates',
    icon: 'IcAward',
    title: strings.tournamentUpdatesTitle,
    subtitle: strings.tournamentUpdatesSubtitle,
  },
  {
    key: 'coGuardianActivity',
    icon: 'IcPeople',
    title: strings.coGuardianActivityTitle,
    subtitle: strings.coGuardianActivitySubtitle,
  },
  // {
  //   key: 'shopDeals',
  //   icon: 'IcShop',
  //   title: strings.shopDealsTitle,
  //   subtitle: strings.shopDealsSubtitle,
  // },
];

/** All opt-ins off — the API default, used when the guardian has no saved preferences. */
const DEFAULT_PREFERENCES: auth.NotificationPreferences = {
  classReminders: false,
  coachNotes: false,
  tournamentUpdates: false,
  coGuardianActivity: false,
  shopDeals: false,
};

type OnboardingStepStayInLoopProps = {
  /** Guardian's saved opt-ins to seed the toggles (from the profile); missing keys default to off. */
  initialPreferences?: Partial<auth.NotificationPreferences> | null;
  /** Bubbles the current toggle state up so the wizard's "Finish" can persist it. */
  onChange: (preferences: auth.NotificationPreferences) => void;
};

type NotificationRowProps = {
  icon: AvailableIconName;
  iconSize?: number;
  title: string;
  subtitle: string;
  titleVariant?: React.ComponentProps<typeof Text>['variant'];
  subtitleVariant?: React.ComponentProps<typeof Text>['variant'];
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
};

/** A single settings row: icon thumbnail, title/subtitle, and a trailing toggle. */
function NotificationRow({
  icon,
  iconSize,
  title,
  subtitle,
  titleVariant = 'Font Guardian App/Subtitle/Subtitle 14 Bold',
  subtitleVariant = 'Font Guardian App/Subtitle/Subtitle 12 Regular',
  value,
  onValueChange,
  disabled = false,
}: NotificationRowProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" gap={scale(10)} style={styles.row}>
      <Stack align="center" justify="center" style={styles.thumb}>
        <SvgIcon name={icon} size={scale(iconSize ?? 24)} color={guardianColors['Main/Main']} />
      </Stack>
      <Stack flex={1} gap={scale(2)}>
        <Text variant={titleVariant} style={styles.rowTitle}>
          {title}
        </Text>
        <Text variant={subtitleVariant} style={styles.rowSubtitle}>
          {subtitle}
        </Text>
      </Stack>
      <Switch value={value} onValueChange={onValueChange} disabled={disabled} />
    </Stack>
  );
}

/**
 * Onboarding step 4 — notification opt-ins + Face ID sign-in. The five notification
 * toggles are seeded from the guardian's saved `notificationPreferences` and their
 * combined state is lifted to the screen so "Finish" can PATCH
 * `/api/v1/guardians/me/notification-preferences`. The Face ID toggle saves the
 * credential captured at sign-in into the biometric-gated Keychain (see `useBiometric`),
 * and is hidden on devices without biometrics.
 */
export function OnboardingStepStayInLoop({
  initialPreferences,
  onChange,
}: OnboardingStepStayInLoopProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const {
    request: requestNotificationPermission,
    isDenied,
    isUndetermined,
    canAsk: canAskNotificationPermission,
    isSupported: isNotificationSupported,
  } = useNotificationPermission();
  const isWeb = Platform.OS === 'web';

  const [preferences, setPreferences] = React.useState<auth.NotificationPreferences>({
    ...DEFAULT_PREFERENCES,
    ...(initialPreferences ?? {}),
  });

  // Face ID sign-in opt-in. `enabled` is the persisted flag; toggling it saves the
  // credential captured at sign-in into the biometric-gated Keychain (or clears it).
  const {
    kind,
    enabled: faceIdEnabled,
    enable: enableBiometric,
    disable: disableBiometric,
  } = useBiometric();

  const handleToggleFaceId = React.useCallback(
    async (next: boolean) => {
      if (next) enableBiometric();
      else disableBiometric();
    },
    [enableBiometric, disableBiometric],
  );

  // Once the guardian's saved preferences are available, seed the toggles from them.
  // Guarded so it runs a single time — later toggles must not be clobbered, and it
  // covers the resume-to-step-4 case where the profile arrives after mount.
  const seededRef = React.useRef(Boolean(initialPreferences));

  // Push the current state up on mount so a "Finish" without touching a toggle still
  // saves the guardian's existing (or default) preferences.
  //
  // On native, also ask the OS for notification permission the moment the step opens — if it's
  // already been blocked for good this is a no-op (no popup) and the callout below covers
  // routing to Settings. Web is excluded: browsers only raise the prompt from inside a user
  // gesture, so an on-mount call is swallowed silently — there the callout is the way in.
  React.useEffect(() => {
    onChange(preferences);
    if (!isWeb) requestNotificationPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (seededRef.current || !initialPreferences) return;
    seededRef.current = true;
    const seeded = { ...DEFAULT_PREFERENCES, ...initialPreferences };
    setPreferences(seeded);
    onChange(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPreferences]);

  const setPreference = (key: keyof auth.NotificationPreferences, value: boolean) => {
    setPreferences(prev => {
      const next = { ...prev, [key]: value };
      onChange(next);
      return next;
    });

    // Switching a preference on is the moment the guardian actually asks for notifications,
    // which makes it the best time to request the permission — and the toggle is a real user
    // gesture, so the browser will honour the prompt. Guarded by `canAsk`, so this never nags
    // someone who already decided (on native the prompt has run on mount, making it a no-op
    // there). The preference itself is independent of the permission: it controls whether the
    // notification is raised at all, in-app included, so a declined prompt leaves the toggle on.
    if (value && canAskNotificationPermission) requestNotificationPermission();
  };

  // Web keeps the callout up while push is still actionable — the guardian hasn't been asked
  // yet (nothing prompted them on mount), or they blocked it and can turn it back on. Browsers
  // with no Notification API are hidden entirely, since there's nothing to act on. Native only
  // needs the denied case, because the prompt already ran on mount.
  const showPermissionCallout = isWeb
    ? isNotificationSupported && (isUndetermined || isDenied)
    : isDenied;

  const handlePermissionCalloutPress = React.useCallback(() => {
    // Native: the OS owns this setting, so open it directly.
    if (!isWeb) {
      Linking.openSettings();
      return;
    }

    // Web, never asked: this runs straight off the tap, which is what satisfies the browser's
    // user-gesture requirement for the prompt.
    if (canAskNotificationPermission) {
      requestNotificationPermission();
      return;
    }

    // Web, blocked: browsers expose no API for opening their own site settings and won't
    // re-prompt once denied, so the guardian has to flip it themselves.
    showAlert({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.common.webNotificationsDeniedMessage,
      buttons: [{ text: STRINGS.common.buttons.dismiss }],
    });
  }, [isWeb, canAskNotificationPermission, requestNotificationPermission]);

  return (
    <Stack>
      <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
        {strings.title}
      </Text>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.subtitle}>
        {strings.subtitle.replace(
          '${kind}',
          kind === 'both-face-and-finger'
            ? 'Biometrics'
            : kind === 'face'
              ? 'Face ID'
              : kind === 'fingerprint'
                ? 'Touch ID'
                : 'Biometrics',
        )}
      </Text>

      <Scroll>
        {/* Notification opt-ins card */}
        <Stack gap={scale(8)} marginTop={scale(24)} style={styles.card}>
          {/* Warning callout — shown while push can't be delivered. Push notifications won't
              arrive while the app is backgrounded/closed, so this explains the guardian will
              still get in-app notifications. Tapping opens the system notification settings on
              native; on web it raises the browser prompt, or explains how to unblock it. */}
          {showPermissionCallout && (
            <Stack style={styles.warningWrapper}>
              <TouchableOpacity activeOpacity={0.8} onPress={handlePermissionCalloutPress}>
                <LinearGradient
                  colors={WARNING_GRADIENT}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.warningGradient}>
                  <Stack padding={scale(8)} style={styles.warning}>
                    <Image source={imgNotiBell} style={styles.warningIcon} />
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                      style={styles.warningText}>
                      {strings.warning}
                    </Text>
                    <SvgIcon name="IcPaginationArrow" size={scale(20)} color={guardianColors[6]} />
                  </Stack>
                </LinearGradient>
              </TouchableOpacity>
            </Stack>
          )}

          {/* Toggles stay interactable regardless of the OS permission: they control whether
              a notification is received at all (including in-app). The OS permission only
              affects push delivery while the app is backgrounded/closed — when it's denied the
              warning callout above explains that only in-app notifications will come through. */}
          {NOTIFICATION_ROWS.map(row => (
            <NotificationRow
              key={row.key}
              icon={row.icon}
              title={row.title}
              subtitle={row.subtitle}
              value={preferences[row.key]}
              onValueChange={value => setPreference(row.key, value)}
            />
          ))}
        </Stack>

        {/* Face ID sign-in card — always shown. On devices without biometrics, toggling on
            surfaces a "not supported" notice instead of enabling. */}
        {Platform.OS !== 'web' && (
          <Stack marginTop={scale(12)} style={styles.card}>
            <NotificationRow
              icon={
                kind === 'both-face-and-finger'
                  ? 'IcFingerPrint'
                  : kind === 'face'
                    ? 'IcFaceId'
                    : 'IcFingerPrint'
              }
              iconSize={20}
              title={strings.faceIdTitle.replace(
                '${kind}',
                kind === 'both-face-and-finger'
                  ? 'Biometrics'
                  : kind === 'face'
                    ? 'FaceID'
                    : kind === 'fingerprint'
                      ? 'TouchID'
                      : 'Biometrics',
              )}
              subtitle={strings.faceIdSubtitle.replace(
                '${kind}',
                kind === 'both-face-and-finger'
                  ? 'Biometrics'
                  : kind === 'face'
                    ? 'Face ID'
                    : kind === 'fingerprint'
                      ? 'Touch ID'
                      : 'Biometrics',
              )}
              value={faceIdEnabled}
              onValueChange={handleToggleFaceId}
            />
          </Stack>
        )}
      </Scroll>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: colors['10'],
  },
  subtitle: {
    marginTop: scale(8),
    color: colors['10'],
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(16),
  },
  warningWrapper: {
    paddingTop: scale(10),
    paddingLeft: scale(12),
    paddingRight: scale(16),
  },
  warningGradient: {
    // padding: scale(8),
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: WARNING_BORDER,
  },
  warning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(7),
    // padding: scale(8),
  },
  warningIcon: {
    width: scale(32),
    height: scale(32),
  },
  warningText: {
    flex: 1,
    color: colors['10'],
  },
  row: {
    minHeight: scale(70),
    paddingLeft: scale(12),
    paddingRight: scale(16),
    paddingVertical: scale(10),
  },
  thumb: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(8),
    backgroundColor: THUMB_BG,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors['1'],
  },
  rowTitle: {
    color: guardianColors['Main/Main'],
  },
  rowSubtitle: {
    color: guardianColors['6'],
  },
}));

export default OnboardingStepStayInLoop;
